import { useState, useEffect, useId } from 'react'
import { ChevronDown, ChevronRight, GripVertical, ImageOff, Plus, Trash2, X } from 'lucide-react'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { WireArticleBlock, WireImageArtifact, WirePhotoAspectRatio, WirePhotoItem } from '../../api/types'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt'

// ─── Types ────────────────────────────────────────────────────────────────────

type PhotoGroupBlock = Extract<WireArticleBlock, { type: 'photo-group' }>

// ─── Option tables ────────────────────────────────────────────────────────────

const WIDTH_OPTIONS: { value: NonNullable<PhotoGroupBlock['width']>; label: string }[] = [
  { value: 'column', label: 'Column' },
  { value: 'wide',   label: 'Wide'   },
  { value: 'full',   label: 'Full'   },
  { value: 'bleed',  label: 'Bleed'  },
]

const RATIO_OPTIONS: { value: WirePhotoAspectRatio; label: string }[] = [
  { value: 'natural', label: 'Natural' },
  { value: '1:1',     label: '1:1'     },
  { value: '4:3',     label: '4:3'     },
  { value: '3:2',     label: '3:2'     },
  { value: '16:9',    label: '16:9'    },
  { value: '2:1',     label: '2:1'     },
  { value: '3:4',     label: '3:4'     },
  { value: '2:3',     label: '2:3'     },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
      {children}
    </p>
  )
}

// ─── Sortable photo item wrapper ──────────────────────────────────────────────

function SortablePhotoItemEditor(props: PhotoItemEditorProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.photo.id,
  })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'opacity-40' : ''}
    >
      <PhotoItemEditor {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  )
}

// ─── Per-photo item editor ────────────────────────────────────────────────────

export interface PhotoItemEditorProps {
  photo: WirePhotoItem
  index: number
  imageUrl: string
  onChange: (updated: WirePhotoItem) => void
  /** When omitted the remove button is hidden — used when the photo is required (e.g. photo-comparison slots). */
  onRemove?: () => void
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>
}

export function PhotoItemEditor({ photo, index, imageUrl, onChange, onRemove, dragHandleProps }: PhotoItemEditorProps) {
  const [open, setOpen] = useState(false)
  const [imgError, setImgError] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [altText,    setAltText]    = useState(photo.altText ?? '')
  const [caption,    setCaption]    = useState(photo.caption ?? '')
  const [credit,     setCredit]     = useState(photo.credit ?? '')
  const [captionCmt, setCaptionCmt] = useState<CmtResult | undefined>(
    photo.captionClassification ? { classification: photo.captionClassification, disseminationControls: photo.captionDisseminationControls ?? [] } : undefined
  )

  // Sync local state if the photo prop is updated externally (e.g. addPhoto
  // carrying over artifact data after mount).
  useEffect(() => {
    setAltText(photo.altText ?? '')
    setCaption(photo.caption ?? '')
    setCredit(photo.credit ?? '')
    setCaptionCmt(photo.captionClassification ? { classification: photo.captionClassification, disseminationControls: photo.captionDisseminationControls ?? [] } : undefined)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.id, photo.caption, photo.captionClassification, photo.altText, photo.credit])

  function patch(p: Partial<WirePhotoItem>) {
    onChange({ ...photo, ...p })
  }

  function commitAltText() {
    const val = altText.trim() || undefined
    if (val !== photo.altText) patch({ altText: val })
  }

  function commitCaption() {
    const val = caption.trim() || undefined
    if (val !== (photo.caption ?? undefined)) patch({ caption: val })
  }

  function commitCredit() {
    const val = credit.trim() || undefined
    if (val !== photo.credit) patch({ credit: val })
  }

  function commitCaptionCmt(result: CmtResult) {
    setCaptionCmt(result)
    patch({ captionClassification: result.classification, captionDisseminationControls: result.disseminationControls.length ? result.disseminationControls : undefined })
  }

  return (
    <div className="group rounded border border-line">
      {/* Collapsed header — drag handle + expand button + delete button */}
      <div className="flex w-full items-center hover:bg-fill">
        {dragHandleProps && (
          <button
            type="button"
            className="cursor-grab touch-none pl-1.5 text-fg-secondary opacity-40 hover:opacity-100 active:cursor-grabbing"
            aria-label="Drag to reorder"
            {...dragHandleProps}
          >
            <GripVertical size={13} />
          </button>
        )}
        <button
          type="button"
          onClick={() => { setOpen(o => !o); setConfirmDelete(false) }}
          className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left"
          aria-expanded={open}
        >
          <span className="text-fg-secondary">
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
          <div className="h-7 w-10 shrink-0 overflow-hidden rounded bg-fill">
            {imgError ? (
              <div className="flex h-full items-center justify-center">
                <ImageOff size={10} className="text-fg-secondary" />
              </div>
            ) : (
              <img
                src={imageUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={() => setImgError(true)}
              />
            )}
          </div>
          <span className="min-w-0 flex-1 truncate text-[11px] text-fg">
            {photo.caption ? photo.caption : <span className="italic text-fg-secondary">Photo {index + 1}</span>}
          </span>
        </button>
        {onRemove && (confirmDelete ? (
          <div className="flex shrink-0 items-center gap-1 pr-2">
            <button
              type="button"
              onClick={onRemove}
              className="rounded bg-red-9 px-1.5 py-0.5 text-[10px] font-semibold text-white hover:bg-red-10"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="rounded border border-line px-1.5 py-0.5 text-[10px] text-fg-secondary hover:bg-canvas"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="mr-1.5 shrink-0 rounded p-0.5 text-fg-secondary opacity-0 transition-opacity hover:text-red-9 group-hover:opacity-100"
            aria-label="Remove photo"
          >
            <Trash2 size={12} />
          </button>
        ))}
      </div>

      {/* Expanded fields */}
      {open && (
        <div className="space-y-3 border-t border-line px-2 py-3">

          {/* Full thumbnail */}
          <div className="overflow-hidden rounded border border-line bg-fill" style={{ height: 72 }}>
            {imgError ? (
              <div className="flex h-full items-center justify-center gap-1 text-fg-secondary">
                <ImageOff size={12} aria-hidden="true" />
                <span className="text-[10px]">Unavailable</span>
              </div>
            ) : (
              <img src={imageUrl} alt="" className="h-full w-full object-cover" />
            )}
          </div>

          {/* Alt text */}
          <div>
            <FieldLabel>Alt Text</FieldLabel>
            <input
              type="text"
              value={altText}
              onChange={e => setAltText(e.target.value)}
              onBlur={commitAltText}
              onKeyDown={e => { if (e.key === 'Enter') commitAltText() }}
              placeholder="Describe the image…"
              className="w-full rounded border border-line bg-fill px-1.5 py-1 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-line focus:outline-none"
            />
          </div>

          {/* Expandable */}
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={photo.expandable ?? false}
              onChange={e => patch({ expandable: e.target.checked })}
              className="h-3.5 w-3.5 rounded"
            />
            <span className="text-xs text-fg">Expandable (lightbox)</span>
          </label>

          {/* Credit */}
          <div>
            <FieldLabel>Credit</FieldLabel>
            <input
              type="text"
              value={credit}
              onChange={e => setCredit(e.target.value)}
              onBlur={commitCredit}
              onKeyDown={e => { if (e.key === 'Enter') commitCredit() }}
              placeholder="Photo by…"
              className="w-full rounded border border-line bg-fill px-1.5 py-1 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-line focus:outline-none"
            />
          </div>

          {/* Caption */}
          <div>
            <FieldLabel>Caption</FieldLabel>
            <textarea
              value={caption}
              onChange={e => setCaption(e.target.value)}
              onBlur={commitCaption}
              rows={2}
              placeholder="Caption text…"
              className="w-full resize-none rounded border border-line bg-fill px-1.5 py-1 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-line focus:outline-none"
            />
          </div>

          {/* Caption portion mark */}
          <div>
            <FieldLabel>Caption Portion Mark</FieldLabel>
            <CmtWrapper value={captionCmt} onChange={commitCaptionCmt} compact />
          </div>

        </div>
      )}
    </div>
  )
}

// ─── Main panel ───────────────────────────────────────────────────────────────

interface Props {
  block: PhotoGroupBlock
  onUpdate: (updated: PhotoGroupBlock) => void
  imageArtifacts: WireImageArtifact[]
}

export function PhotoGroupBlockPanel({ block, onUpdate, imageArtifacts }: Props) {
  const dndId = useId()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  )

  const [groupTitle,    setGroupTitle]    = useState(block.groupTitle ?? '')
  const [titleCmt,      setTitleCmt]      = useState<CmtResult | undefined>(
    block.groupTitleClassification ? { classification: block.groupTitleClassification, disseminationControls: block.groupTitleDisseminationControls ?? [] } : undefined
  )
  const [pickerOpen,    setPickerOpen]    = useState(false)

  const width       = block.width       ?? 'column'
  const aspectRatio = block.aspectRatio ?? 'natural'
  const displayType      = block.display.type
  const gridColumns      = block.display.type === 'grid' ? (block.display.columns ?? 2) : 2
  const carouselStyle    = block.display.type === 'carousel' ? (block.display.style ?? 'default') : 'default'
  const carouselParallax = block.display.type === 'carousel' ? (block.display.parallax ?? false) : false

  function patchBlock(patch: Partial<PhotoGroupBlock>) {
    onUpdate({ ...block, ...patch })
  }

  function updatePhoto(index: number, updated: WirePhotoItem) {
    const photos = block.photos.map((p, i) => i === index ? updated : p)
    onUpdate({ ...block, photos })
  }

  function removePhoto(index: number) {
    onUpdate({ ...block, photos: block.photos.filter((_, i) => i !== index) })
  }

  function addPhoto(artifact: WireImageArtifact) {
    const newPhoto: WirePhotoItem = {
      id: crypto.randomUUID(),
      artifactId: artifact.id,
      classification: artifact.classification,
      captionClassification: artifact.captionClassification,
      captionDisseminationControls: artifact.captionDisseminationControls,
      caption: artifact.caption,
      altText: artifact.altText,
      credit: artifact.credit,
      focalPoint: artifact.focalPoint,
      expandable: false,
    }
    onUpdate({ ...block, photos: [...block.photos, newPhoto] })
    setPickerOpen(false)
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = block.photos.findIndex(p => p.id === active.id)
    const newIndex = block.photos.findIndex(p => p.id === over.id)
    if (oldIndex !== -1 && newIndex !== -1) {
      onUpdate({ ...block, photos: arrayMove(block.photos, oldIndex, newIndex) })
    }
  }

  function commitGroupTitle() {
    const val = groupTitle.trim() || undefined
    if (val !== block.groupTitle) patchBlock({ groupTitle: val })
  }

  function commitTitleCmt(result: CmtResult) {
    setTitleCmt(result)
    patchBlock({ groupTitleClassification: result.classification, groupTitleDisseminationControls: result.disseminationControls.length ? result.disseminationControls : undefined })
  }

  return (
    <div className="space-y-4">

      {/* Width */}
      <div>
        <FieldLabel>Width</FieldLabel>
        <div className="flex gap-1">
          {WIDTH_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => patchBlock({ width: opt.value })}
              className={`rounded border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                width === opt.value
                  ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                  : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Aspect ratio */}
      <div>
        <FieldLabel>Aspect Ratio</FieldLabel>
        <select
          value={aspectRatio}
          onChange={e => patchBlock({ aspectRatio: e.target.value as WirePhotoAspectRatio })}
          className="w-full rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-accent-primary-line focus:outline-none"
        >
          {RATIO_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.value === 'natural' ? 'Natural — preserve original' : opt.label}</option>
          ))}
        </select>
      </div>

      {/* Display type */}
      <div>
        <FieldLabel>Display</FieldLabel>
        <div className="flex gap-1 mb-2">
          {(['grid', 'carousel'] as const).map(type => (
            <button
              key={type}
              type="button"
              onClick={() => {
                if (type === 'grid') patchBlock({ display: { type: 'grid', columns: gridColumns } })
                else patchBlock({ display: { type: 'carousel', style: carouselStyle, parallax: carouselParallax } })
              }}
              className={`rounded border px-2 py-0.5 text-[11px] font-semibold capitalize transition-colors ${
                displayType === type
                  ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                  : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
              }`}
            >
              {type === 'carousel' ? 'Carousel' : 'Grid'}
            </button>
          ))}
        </div>

        {/* Grid column count */}
        {displayType === 'grid' && (
          <div>
            <FieldLabel>Columns</FieldLabel>
            <div className="flex gap-1">
              {([2, 3, 4] as const).map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => patchBlock({ display: { type: 'grid', columns: n } })}
                  className={`rounded border px-3 py-0.5 text-[11px] font-semibold transition-colors ${
                    gridColumns === n
                      ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                      : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Carousel sub-options */}
        {displayType === 'carousel' && (
          <div className="space-y-3">

            {/* Carousel style */}
            <div>
              <FieldLabel>Style</FieldLabel>
              <div className="flex gap-1">
                {([
                  { value: 'default',    label: 'Default'    },
                  { value: 'thumbnails', label: 'Thumbnails' },
                ] as const).map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => patchBlock({ display: { type: 'carousel', style: opt.value, parallax: carouselParallax } })}
                    className={`rounded border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                      carouselStyle === opt.value
                        ? 'border-accent-primary-solid bg-accent-primary-fill text-accent-primary-fg'
                        : 'border-line bg-canvas text-fg-secondary hover:bg-fill'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="mt-0.5 text-[10px] text-fg-secondary">
                {carouselStyle === 'thumbnails'
                  ? 'Main slide above a scrollable thumbnail strip'
                  : 'Single full-size slide with prev/next arrows'}
              </p>
            </div>

            {/* Parallax */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={carouselParallax}
                onChange={e => patchBlock({ display: { type: 'carousel', style: carouselStyle, parallax: e.target.checked } })}
                className="h-3.5 w-3.5 rounded"
              />
              <div>
                <span className="text-xs text-fg">Parallax effect</span>
                <p className="text-[10px] text-fg-secondary">Images translate at half scroll speed for depth</p>
              </div>
            </label>

          </div>
        )}
      </div>

      {/* Group title */}
      <div>
        <FieldLabel>Group Title</FieldLabel>
        <input
          type="text"
          value={groupTitle}
          onChange={e => setGroupTitle(e.target.value)}
          onBlur={commitGroupTitle}
          onKeyDown={e => { if (e.key === 'Enter') commitGroupTitle() }}
          placeholder="Optional label above group…"
          className="w-full rounded border border-line bg-fill px-1.5 py-1 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-line focus:outline-none"
        />
      </div>

      {/* Group title portion mark */}
      {groupTitle.trim() && (
        <div>
          <FieldLabel>Title Portion Mark</FieldLabel>
          <CmtWrapper value={titleCmt} onChange={commitTitleCmt} compact />
        </div>
      )}

      {/* Divider before per-photo list */}
      <div className="border-t border-line pt-2">
        <FieldLabel>Photos ({block.photos.length})</FieldLabel>
        <DndContext
          id={dndId}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={block.photos.map(p => p.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-1">
              {block.photos.map((photo, i) => (
                <SortablePhotoItemEditor
                  key={photo.id}
                  photo={photo}
                  index={i}
                  imageUrl={imageArtifacts.find(a => a.id === photo.artifactId)?.presignedUrl ?? ''}
                  onChange={(updated) => updatePhoto(i, updated)}
                  onRemove={() => removePhoto(i)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Add photo picker */}
        <div className="mt-2">
          {pickerOpen ? (
            <div className="rounded border border-line bg-canvas">
              <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
                  Add from artifacts
                </p>
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
                  aria-label="Close picker"
                >
                  <X size={12} />
                </button>
              </div>
              {imageArtifacts.length === 0 ? (
                <p className="px-2 py-3 text-[11px] text-fg-secondary">
                  No image artifacts uploaded yet.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-1.5 p-2">
                  {imageArtifacts.map(artifact => (
                    <button
                      key={artifact.id}
                      type="button"
                      onClick={() => addPhoto(artifact)}
                      className="overflow-hidden rounded border border-line bg-fill transition-colors hover:border-accent-primary-solid"
                      title={artifact.filename}
                    >
                      <div className="aspect-square overflow-hidden">
                        <img
                        src={imageArtifacts.find(a => a.id === artifact.id)?.presignedUrl ?? ''}
                          alt={artifact.altText ?? ''}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <p className="truncate px-1 py-0.5 text-[9px] text-fg-secondary">
                        {artifact.filename}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-line py-1.5 text-[11px] text-fg-secondary transition-colors hover:border-accent-primary-solid hover:text-fg"
            >
              <Plus size={11} />
              Add Photo
            </button>
          )}
        </div>
      </div>

    </div>
  )
}
